import { CELLARS, DUNGEON, SKILLS } from '@somnhold/content';
import { hexToWorld } from '../hex.ts';
import { dist2, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, ExpDevice, Expedition, ExpWing, Hero } from '../types.ts';
import { targetable } from '../battle/combat.ts';
import { expCarry, expRoomAt } from '../battle/expedition.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { clericCare, useSkills } from './ghost.ts';

/*
 * The garrison cellars as a capable group plays them (balance runs only): one wing each, by hero order; a hero
 * whose second room is still shut helps another wing's first; past the floodgate everyone fells what guards the
 * bell, the first two carry it to the winch and one cranks while the rest fight.
 */

const standing = (h: Hero): boolean => h.downTicks === 0;
const at = (d: { q: number; r: number }): { x: number; y: number } => hexToWorld(d.q, d.r);
const WINGS: ExpWing[] = ['tower', 'cistern', 'powder', 'cells'];

const go = (s: BattleState, h: Hero, p: { x: number; y: number }, near = 300): boolean => {
  if (withinRange(h.x, h.y, p.x, p.y, near)) {
    if (h.moving && withinRange(h.x, h.y, p.x, p.y, 200)) h.moving = false;
    return true;
  }
  if (!h.moving || Math.abs(h.destX - p.x) + Math.abs(h.destY - p.y) > 300) orderMove(s, h, p.x, p.y);
  return false;
};

const nearest = (h: Hero, es: Enemy[]): Enemy | undefined => {
  let best: Enemy | undefined;
  let bd = 0x7fffffff;
  for (const e of es) {
    const d = dist2(h.x, h.y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

/** Nightmares in the room the hero stands in. */
const foesHere = (s: BattleState, h: Hero): Enemy[] => {
  const i = expRoomAt(s, h.x, h.y);
  return i < 0 ? [] : s.enemies.filter((e) => e.hp > 0 && e.room === i && targetable(s, e));
};

const fight = (s: BattleState, h: Hero, foes: Enemy[]): void => {
  const target = nearest(h, foes);
  if (!target) return;
  useSkills(s, h, target);
  if (h.cls !== 'cleric' && h.skillCd[2] === 0 && foes.length >= 3) castSkill(s, h, { t: 'skill', slot: 2, x: target.x, y: target.y, id: target.id });
  if (h.target !== target.id) {
    h.target = target.id;
    h.forced = true;
    h.moving = false;
    h.path = [];
  }
};

const dev = (x: Expedition, kind: ExpDevice['kind'], wing?: ExpWing): ExpDevice | undefined => x.devices.find((d) => d.kind === kind && (!wing || d.wing === wing));

/** The first-room job of a wing, if it isn't done: where to stand (or what to do). */
const firstJob = (s: BattleState, x: Expedition, h: Hero, wing: ExpWing, helper: number): boolean => {
  if (wing === 'tower') {
    const lever = dev(x, 'lever');
    if (!lever || lever.done) return false;
    go(s, h, at(lever));
    return true;
  }
  if (wing === 'cistern') {
    const wheels = x.devices.filter((d) => d.kind === 'wheel' && !d.done);
    const w = wheels[helper % (wheels.length || 1)];
    if (!w) return false;
    go(s, h, at(w));
    return true;
  }
  if (wing === 'powder') {
    const rubble = dev(x, 'rubble');
    if (!rubble || rubble.done) return false;
    const mine = x.items.find((it) => it.id === h.carry);
    if (mine) {
      if (go(s, h, at(rubble), CELLARS.barrel.rubbleRange - 400)) expCarry(s, h);
      return true;
    }
    const barrel = x.items.find((it) => it.kind === 'barrel');
    if (barrel) {
      // Someone else has it, or it is lit: keep clear of the blast.
      if (barrel.carriers.length || barrel.fuse > 0) return false;
      if (go(s, h, barrel, CELLARS.carry.reach - 300)) expCarry(s, h);
      return true;
    }
    const stack = dev(x, 'barrels');
    if (stack && go(s, h, at(stack), CELLARS.carry.reach - 300)) expCarry(s, h);
    return true;
  }
  const key = x.items.find((it) => it.kind === 'key');
  const lock = dev(x, 'lock', 'cells');
  if (!key || !lock || lock.done) return false;
  if (h.carry === key.id) {
    if (go(s, h, at(lock), CELLARS.barrel.rubbleRange - 300)) expCarry(s, h);
    return true;
  }
  if (key.carriers.length) return false;
  if (go(s, h, key, CELLARS.carry.reach - 300)) expCarry(s, h);
  return true;
};

/** The second-room job: the wing's lock, once its room is open. */
const lockJob = (s: BattleState, x: Expedition, h: Hero, wing: ExpWing): boolean => {
  if (wing === 'cells') return false;
  const lock = dev(x, 'lock', wing);
  if (!lock || lock.done) return false;
  const door = x.links.findIndex(([, b]) => b === `${wing}B`);
  if (!x.open[door]) return false;
  go(s, h, at(lock));
  return true;
};

/** Past the floodgate: fell what is in the cistern, the first two carry the bell, one cranks the winch. */
const bellJob = (s: BattleState, x: Expedition, h: Hero, up: Hero[]): boolean => {
  const bell = x.items.find((it) => it.kind === 'bell');
  const winch = dev(x, 'winch');
  if (!bell || !winch) return false;
  if (bell.hooked) {
    if (up[0] === h) {
      go(s, h, at(winch), 500);
      return true;
    }
    return false;
  }
  const room = x.rooms.cistern;
  if (s.enemies.some((e) => e.hp > 0 && e.room === room && e.elite)) return false;
  const bearers = up.slice(0, 2);
  if (!bearers.includes(h)) return false;
  if (h.carry === bell.id) {
    if (bell.carriers.length < 2) {
      h.moving = false;
      return true;
    }
    if (go(s, h, at(winch), CELLARS.winch.range - 500)) expCarry(s, h);
    return true;
  }
  if (go(s, h, bell, CELLARS.carry.reach - 300)) expCarry(s, h);
  return true;
};

export const cellarsBot = (s: BattleState, h: Hero): void => {
  const x = s.dungeon?.exp;
  if (!x || !standing(h) || h.stun > 0) return;
  const up = s.heroes.filter(standing);
  // A cleric with dawn ready raises the fallen around it.
  if (h.cls === 'cleric' && h.skillCd[2] === 0 && s.heroes.some((o) => o.downTicks > 0 && withinRange(h.x, h.y, o.x, o.y, SKILLS.dawn.radius))) castSkill(s, h, { t: 'skill', slot: 2 });
  // A fallen ally in the same room: pick them up.
  const fallen = s.heroes.find((o) => o.downTicks > 0 && expRoomAt(s, o.x, o.y) === expRoomAt(s, h.x, h.y));
  if (fallen && h.carry === undefined) {
    go(s, h, fallen, DUNGEON.reviveRange - 300);
    return;
  }
  const carrying = h.carry !== undefined;
  const foes = foesHere(s, h);
  // Hands full: get the load where it goes, fight later (a barrel struck too often goes off).
  if (!carrying && foes.length) {
    fight(s, h, foes);
    return;
  }
  if (h.target) h.target = 0;
  if (x.gateOpen) {
    if (bellJob(s, x, h, up)) return;
    // The rest guard the bell: to the cistern, or the hub once it is up there.
    const bell = x.items.find((it) => it.kind === 'bell');
    if (bell) go(s, h, bell, 1500);
    return;
  }
  // Before the floodgate hall opens, everyone goes on down.
  const hubDoor = x.links.findIndex(([a, b]) => a === 'guard' && b === 'hub');
  if (!x.open[hubDoor]) {
    go(s, h, at(s.map.dungeon?.rooms[x.rooms.guard] ?? { q: 0, r: 0 }), 1500);
    return;
  }
  const wings = WINGS.filter((w) => w !== 'cells' || x.rooms.cells >= 0);
  const me = up.indexOf(h);
  const mine = wings[me % wings.length] as ExpWing;
  if (lockJob(s, x, h, mine) || firstJob(s, x, h, mine, 0)) return;
  // My wing waits on another: help the first one that still needs hands, then any lock that is open.
  for (const w of wings) if (w !== mine && firstJob(s, x, h, w, 1)) return;
  for (const w of wings) if (lockJob(s, x, h, w)) return;
  if (h.cls === 'cleric') clericCare(s, h);
};
